import { getChainFromId, getChainRpcUrls } from './config.mjs'

/** chainId -> 当前使用的 rpc 在列表中的下标 */
const activeRpcIndex = {}

/** 单次 RPC 读请求超时（毫秒）；避免首个节点挂死导致无法切换 */
export const RPC_CALL_TIMEOUT_MS = 12000

export function resolveActiveRpcUrl(chainId, chainConf = null) {
  const conf = chainConf || getChainFromId(chainId)
  const urls = getChainRpcUrls(conf)
  if (!urls.length) return ''
  const id = Number(chainId)
  const idx = activeRpcIndex[id] ?? 0
  return urls[idx % urls.length]
}

export function getActiveRpcMeta(chainId, chainConf = null) {
  const conf = chainConf || getChainFromId(chainId)
  const urls = getChainRpcUrls(conf)
  if (!urls.length) return { url: '', index: 0, total: 0 }
  const id = Number(chainId)
  const idx = activeRpcIndex[id] ?? 0
  return {
    url: urls[idx % urls.length],
    index: idx + 1,
    total: urls.length,
  }
}

export function setActiveRpcIndex(chainId, index, chainConf = null) {
  const conf = chainConf || getChainFromId(chainId)
  const urls = getChainRpcUrls(conf)
  if (!urls.length) return ''
  const id = Number(chainId)
  const idx = Math.max(0, Math.min(Number(index), urls.length - 1))
  activeRpcIndex[id] = idx
  return urls[idx]
}

export function resolveRpcUrlAtIndex(chainId, index, chainConf = null) {
  const conf = chainConf || getChainFromId(chainId)
  const urls = getChainRpcUrls(conf)
  if (!urls.length) return ''
  const idx = Math.max(0, Math.min(Number(index), urls.length - 1))
  return urls[idx]
}

export function rotateActiveRpc(chainId, chainConf = null) {
  const conf = chainConf || getChainFromId(chainId)
  const urls = getChainRpcUrls(conf)
  if (urls.length <= 1) return resolveActiveRpcUrl(chainId, conf)
  const id = Number(chainId)
  activeRpcIndex[id] = ((activeRpcIndex[id] ?? 0) + 1) % urls.length
  const meta = getActiveRpcMeta(id, conf)
  console.warn('[hieswap] RPC failover → line', meta.index, '/', meta.total)
  return resolveActiveRpcUrl(id, conf)
}

function extractRpcErrorCode(err) {
  const candidates = [err?.code, err?.statusCode, err?.data?.code, err?.error?.code, err?.cause?.code]
  for (const c of candidates) {
    const n = Number(c)
    if (Number.isFinite(n)) return n
  }
  return null
}

export function isRpcRateLimitError(err) {
  const code = extractRpcErrorCode(err)
  if (code === 429) return true
  const m = String(err?.message ?? err ?? '').toLowerCase()
  return /429|too many requests|rate limit|limit exceeded/.test(m)
}

export function isRpcTransportError(err) {
  const m = String(err?.message ?? err ?? '').toLowerCase()
  if (/execution reverted|revert|invalid opcode|out of gas|insufficient funds|call exception/.test(m)) {
    return false
  }
  if (/user denied|user rejected|invalid sender|wallet account mismatch/.test(m)) {
    return false
  }
  const code = extractRpcErrorCode(err)
  if (code === 401 || code === 403 || code === 408 || code === 429 || code === 502 || code === 503 || code === 504) {
    return true
  }
  if (code === -32603 || code === -32000 || code === -32002 || code === -32005 || code === -32016) {
    return true
  }
  return (
    /failed to fetch|network|timeout|timed out|time-out|econnreset|econnrefused|socket hang up|502|503|504|bad gateway|service unavailable|gateway timeout|could not detect network|invalid json|invalid response|no response|missing response|unexpected end|fetch error|connection|enotfound|etimedout|429|rate limit|internal error|header not found|server error|provider not connected|xhr error|network request failed|load failed|aborted|abort|forbidden|unauthorized|limit exceeded|too many requests|unavailable|cannot connect|not connected|connection reset|connection closed|err_connection|ssl|certificate|dns|getaddrinfo|ecconn|enotreachable|operation was aborted|request failed with status|http error|bad response|malformed|syntaxerror|parse error|empty response|returned error|unknown json|no nodes available|block not found|filter not found|method not found|resource not found|temporarily unavailable|overloaded|busy|maintenance|cors policy|access-control-allow-origin|cross-origin|blocked by cors/.test(
      m
    ) ||
    err?.code === 'NETWORK_ERROR' ||
    err?.code === 'ECONNRESET' ||
    err?.code === 'ETIMEDOUT' ||
    err?.code === 'ENOTFOUND' ||
    err?.code === -32603 ||
    err?.statusCode === 502 ||
    err?.statusCode === 503
  )
}

export function withRpcTimeout(promise, ms = RPC_CALL_TIMEOUT_MS, label = 'RPC') {
  let timer
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const err = new Error(`${label} timeout after ${ms}ms`)
      err.code = 'ETIMEDOUT'
      reject(err)
    }, ms)
  })
  return Promise.race([promise, timeout]).finally(() => {
    if (timer) clearTimeout(timer)
  })
}

/**
 * 配置了多条 RPC 的链：读链失败/超时/节点异常时自动切到下一个节点重试。
 */
export async function withRpcFailover(chainId, fn, opts = {}) {
  const conf = getChainFromId(chainId)
  const urls = getChainRpcUrls(conf)
  const maxTries = Math.max(urls.length, 1)
  const timeoutMs = opts.timeoutMs ?? RPC_CALL_TIMEOUT_MS
  let lastErr
  for (let attempt = 0; attempt < maxTries; attempt++) {
    const rpc = resolveActiveRpcUrl(chainId, conf)
    try {
      return await withRpcTimeout(fn(rpc), timeoutMs, `RPC(${Number(chainId)})`)
    } catch (e) {
      lastErr = e
      if (isRpcTransportError(e) && attempt < maxTries - 1) {
        rotateActiveRpc(chainId, conf)
        continue
      }
      throw e
    }
  }
  throw lastErr
}
